<script setup lang="ts">
    import type { Product } from '~/graphql/generated';

    export interface ProductSummaryProps {
        product: Partial<Product>;
        colorOptionIndex?: number;
        title?: string;
        description?: string;
        baseUrl?: string;
    }
    const props = withDefaults(defineProps<ProductSummaryProps>(), {
        colorOptionIndex: 1,
    });

    const {
        addToCartClicked,
        addToCartDisabled,
        bikeDetail,
        cartBusy,
        changeHoverColorOption,
        colorOption,
        colorOptionChanged,
        config,
        currentImage,
        handleAddToCart,
        product,
        variant,
    } = useProductCard(props.product);

    const variantUrl = computed(() => {
        if (!props.baseUrl) return;
        return `${props.baseUrl}?variant=${config?.variantUrlQuery.value}`;
    });

    if (colorOption.value) {
        const colorIndexToSelect = Math.max(1, Math.min(colorOption.value.stringValues.length ?? 1, props.colorOptionIndex));
        colorOptionChanged(colorOption.value.stringValues[colorIndexToSelect - 1]);
    }
</script>
<template>
    <atm-container class="org-product-summary grid py-8 md:py-10">
        <lazy-atm-heading
            v-if="title"
            class="title">
            <span v-html="woomTextFormat(title)"></span>
        </lazy-atm-heading>
        <nuxt-link
            class="image flex flex-col"
            :to="$helpers.generateLocalePath(variantUrl)">
            <lazy-atm-image
                v-if="currentImage"
                :src="currentImage"
                :alt="removeWoomTextFormat(title)"
                class="aspect-3/2"
                provider="assets"
                sizes="640 sm:768 md:640 lg:792"
                cover />
        </nuxt-link>

        <div class="details flex flex-col gap-4">
            <div
                v-if="description"
                v-html="woomTextFormat(description)"></div>
            <mol-product-card-options
                :config="config"
                :change-hover-color-option="changeHoverColorOption"
                :color-option-changed="colorOptionChanged" />
            <div>
                <lazy-atm-product-card-bike-details
                    v-if="bikeDetail"
                    :bike-detail="bikeDetail" />
                <lazy-atm-price
                    v-if="product.price"
                    :price="config?.selected.value?.price"
                    :compare-at-price="config?.selected.value?.compareAtPrice" />
            </div>
            <mol-product-card-buttons
                :addToCartDisabled="addToCartDisabled"
                :product="product"
                :variant="variant"
                :cartBusy="cartBusy"
                :addToCartClicked="addToCartClicked"
                :handleAddToCart="() => handleAddToCart('product_summary')"
                :pdpUrl="variantUrl"
                size="medium" />
        </div>
    </atm-container>
</template>

<style scoped>
    .org-product-summary {
        grid-template-areas:
            'title title'
            'image image'
            'details details';

        grid-template-rows: auto 1fr;
        @media screen(md) {
            grid-template-areas:
                'image image image image image image title title title title title title'
                'image image image image image image details details details details details details';
        }
        @media screen(xl) {
            grid-template-areas:
                'image image image image image image image image title title title title'
                'image image image image image image image image details details details details';
        }

        .title {
            grid-area: title;
        }
        .image {
            grid-area: image;
        }
        .details {
            grid-area: details;
        }
    }
</style>
